import React, { useState, useEffect } from "react";
import GavelIcon from "@mui/icons-material/Gavel";
import "../styles/TermsStyles.css";

const sections = [
  {
    id: "01",
    title: "Use of Services",
    content:
      "You agree to use Tecniqa's platform solely for lawful purposes and in accordance with all applicable local, state, and national laws and regulations. Any unauthorized use, including but not limited to scraping, automated querying, or reverse engineering of our platform, is strictly prohibited.",
  },
  {
    id: "02",
    title: "Account Responsibility",
    content:
      "You are solely responsible for maintaining the confidentiality of your account credentials and for all activities conducted under your account. Tecniqa shall not be liable for any loss or damage arising from unauthorized access to your account due to your failure to keep credentials secure.",
  },
  {
    id: "03",
    title: "Product Listings & Accuracy",
    content:
      "Tecniqa makes every effort to ensure that product descriptions, specifications, pricing, and availability are accurate. However, we do not warrant that all information is error-free. In the event of a pricing error or mismatch in product specifications, Tecniqa reserves the right to cancel or modify an order and will notify the buyer promptly.",
  },
  {
    id: "04",
    title: "Orders, Payments & Invoicing",
    content:
      "All orders placed through the platform are subject to acceptance by Tecniqa. We reserve the right to refuse or cancel any order at our discretion. Payment must be completed in full before goods are dispatched unless a credit arrangement has been formally established in writing. GST invoices will be issued for all transactions in compliance with Indian tax law.",
  },
  {
    id: "05",
    title: "Shipping, Delivery & Risk",
    content:
      "Delivery timelines are indicative and not guaranteed. Risk of loss or damage to goods passes to the buyer upon handover to the carrier or logistics partner. Tecniqa is not liable for delays caused by courier failures, force majeure, strikes, or customs clearance. Any claims for transit damage must be reported within 48 hours of receipt with supporting photographic evidence.",
  },
  {
    id: "06",
    title: "Returns, Replacements & Warranty",
    content:
      "Returns are accepted only for products that are defective, damaged in transit, or incorrectly supplied. Return requests must be raised within 7 days of delivery. Products must be returned in original, unused condition with all original packaging and documentation. Warranty terms are governed by the respective manufacturer's policy, and Tecniqa acts solely as a facilitator for warranty claims.",
  },
  {
    id: "07",
    title: "Industrial & Safety Compliance",
    content:
      "All machinery, equipment, and industrial products listed on Tecniqa must be installed, operated, and maintained in accordance with the manufacturer's guidelines and applicable safety standards (BIS, IS, CE, or equivalent). Buyers are solely responsible for ensuring regulatory compliance in their jurisdiction. Tecniqa disclaims any liability arising from improper installation or operation of purchased equipment.",
  },
  {
    id: "08",
    title: "Bulk Orders & Custom Procurement",
    content:
      "Bulk orders and custom procurement requests are subject to separate quotation terms, lead times, and payment schedules. Any verbal or informal commitments are not binding unless confirmed in a written purchase order accepted by Tecniqa. Cancellations of custom or bulk orders may attract a restocking or processing fee.",
  },
  {
    id: "09",
    title: "Limitation of Liability",
    content:
      "To the maximum extent permitted by law, Tecniqa's total liability for any claim arising from the use of our platform or products shall not exceed the amount paid by the buyer for the specific product giving rise to the claim. Tecniqa shall not be liable for any indirect, incidental, consequential, or punitive damages, including loss of profit or business interruption.",
  },
  {
    id: "10",
    title: "Intellectual Property",
    content:
      "All content, branding, product catalogues, imagery, and materials published on the Tecniqa platform are protected under applicable copyright and intellectual property laws. Unauthorized reproduction, redistribution, or commercial use of any content without prior written consent from Tecniqa is strictly prohibited.",
  },
  {
    id: "11",
    title: "Privacy & Data Usage",
    content:
      "By using our platform, you consent to the collection and processing of your personal and business information as described in our Privacy Policy. We do not sell your data to third parties. Data shared during the ordering or onboarding process is used exclusively for service delivery and legal compliance.",
  },
  {
    id: "12",
    title: "Governing Law & Dispute Resolution",
    content:
      "These Terms are governed by the laws of India. Any disputes arising out of or in connection with these Terms shall first be attempted to be resolved through mutual negotiation. If unresolved, disputes shall be subject to the exclusive jurisdiction of the courts in Bhubaneswar, Odisha.",
  },
  {
    id: "13",
    title: "Changes to Terms",
    content:
      "Tecniqa reserves the right to update or amend these Terms of Service at any time without prior notice. The latest version will always be published on this page with a revised effective date. Continued use of the platform after any changes constitutes your acceptance of the updated Terms.",
  },
];

export default function TermsOfService() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    document.title = "Terms of Service | Tecniqa";
  }, []);

  return (
    <div className="tp-root">

      {/* HERO */}
      <div className="tp-hero">
        <div className="tp-hero-grid" />
        <div className="tp-hero-orb tp-orb1" />
        <div className="tp-hero-orb tp-orb2" />

        <div className="tp-hero-content">

          <div className="tp-hero-badge">
            <div className="tp-badge-dot" />
            Terms of Service
          </div>

          <h1 className="tp-hero-title">
            Clear Terms,<br />
            <span>Trusted Platform</span>
          </h1>

          <p className="tp-hero-sub">
            Please read these terms carefully before using the Tecniqa platform or placing any orders.
          </p>

          <div className="tp-hero-meta">
            <span className="tp-meta-badge">
              <GavelIcon style={{ fontSize: 13 }} />
              Effective: May 2026
            </span>
            <span className="tp-meta-badge">13 Sections</span>
          </div>

        </div>
      </div>

      {/* BODY */}
      <div className="tp-body">

        {/* INTRO CARD */}
        <div className="tp-intro-card">
          <p>
            Welcome to <strong>Tecniqa</strong> — India's trusted industrial and machinery supply platform.
            By accessing or using our website, placing orders, or engaging with our services, you confirm
            that you have read, understood, and agree to be bound by the following Terms of Service.
            If you do not agree, please discontinue use of the platform.
          </p>
        </div>

        {/* ACCORDION */}
        <div className="tp-sections">
          {sections.map((sec) => {
            const isOpen = active === sec.id;
            return (
              <div
                key={sec.id}
                className={`tp-section${isOpen ? " tp-section--open" : ""}`}
                onClick={() => setActive(isOpen ? null : sec.id)}
              >
                <div className="tp-section-header">
                  <div className="tp-section-left">
                    <span className="tp-section-num">{sec.id}</span>
                    <span className="tp-section-title">{sec.title}</span>
                  </div>
                  <span className="tp-chevron">{isOpen ? "−" : "+"}</span>
                </div>
                {isOpen && (
                  <div className="tp-section-body">
                    <p>{sec.content}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* FOOTER NOTE */}
        <div className="tp-footer-note">
          <div className="tp-footer-note-inner">
            <span className="tp-footer-label">Questions about these terms?</span>
            <p>
              Reach our legal & compliance team at{" "}
              <a href="mailto:support@tecniqa.com">support@tecniqa.com</a> or call{" "}
              <a href="tel:+919668246683">+91 9668 246 683</a> during business hours (Mon–Sat, 9 AM – 6 PM).
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}